import { type StatusMark, STATUS_TONES } from '../../shared/status-marks.ts';
import { OFFLINE_DB_PREFIX, OFFLINE_DB_VERSION, OFFLINE_STORES } from './constants.ts';

// The offline copy's database, written straight onto the IndexedDB API.
//
// One database per signed-in account, named after the account's id, so two
// people sharing a phone never see each other's sites and signing out can
// delete the whole thing rather than pick rows out of it. Four object
// stores:
//
//   entities  one row per read, keyed by the read's own path under /api
//             (`rooms/<id>`, `rooms/<id>/notes`), holding the reply exactly
//             as it came, the row's `updatedAt` where the reply carries one,
//             and when it was kept.
//   queue     the writes waiting for a connection, auto-numbered so they
//             replay in the order they were made.
//   blobs     photo thumbnails and files of the properties being kept.
//   meta      one row: the properties kept and when the manifest last ran.
//
// IndexedDB's callback API is wrapped once here into promises; nothing above
// this file touches a request object.

/** A read kept for when there is no signal. */
export interface StoredEntity {
  /** The read's path under /api, which for a node is its type and id. */
  readonly key: string;
  /** The reply exactly as the API sent it. */
  readonly body: unknown;
  /** The row's version, for the next write's `If-Match`; null when the reply carries none. */
  readonly updatedAt: string | null;
  /** When this copy was taken. */
  readonly keptAt: string;
}

/** What a queued write is waiting to do. */
export type QueuedStatus = 'queued' | 'sending' | 'failed' | 'conflict';

/**
 * The sync state as the app-wide status mark (ROADMAP P6), so a queued write
 * reads with the same colours and shapes as a port or a tester's verdict.
 * Waiting and sending are the neutral and the information tone; a refused
 * write is a fault; a conflict is hatched, because it is waiting on a
 * decision rather than on a connection.
 */
const QUEUED_STATUS_MARKS: Readonly<Record<QueuedStatus, StatusMark>> = {
  queued: {
    id: 'queued',
    label: 'Waiting',
    token: STATUS_TONES.neutral,
    shape: 'outline',
  },
  sending: { id: 'sending', label: 'Sending', token: STATUS_TONES.info, shape: 'dashed' },
  failed: { id: 'failed', label: 'Refused', token: STATUS_TONES.bad, shape: 'filled' },
  conflict: { id: 'conflict', label: 'Conflict', token: STATUS_TONES.warn, shape: 'striped' },
};

export function queuedStatusMark(status: QueuedStatus): StatusMark {
  return QUEUED_STATUS_MARKS[status];
}

/** A file part of a queued upload, kept as bytes so it survives a reload. */
export interface QueuedFile {
  readonly field: string;
  readonly blob: Blob;
  readonly filename: string;
}

/** One write waiting for a connection. */
export interface QueuedWrite {
  /** Auto-numbered by the store; the order writes replay in. */
  readonly id?: number;
  readonly method: string;
  readonly path: string;
  /** A JSON body, or null for a write that carries a form or nothing. */
  readonly body: unknown;
  /** The text parts of a multipart body; null for a JSON write. */
  readonly form: Readonly<Record<string, string>> | null;
  /** The file parts of a multipart body. */
  readonly files: readonly QueuedFile[];
  /** The UUID that makes replaying this write safe. */
  readonly idempotencyKey: string;
  /** The version the write was made against, sent as `If-Match`; null when there was none. */
  readonly ifMatch: string | null;
  /** The entity the write is about, so its cached copy can be put back on a discard. */
  readonly entity: string;
  /** The ids this write creates, so an operation after it can point at them. */
  readonly createdIds: readonly string[];
  readonly createdAt: string;
  readonly attempts: number;
  /** The earliest moment the next attempt may be made; '' while none is owed. */
  readonly nextAttemptAt: string;
  readonly lastError: string;
  readonly status: QueuedStatus;
  /** The server's copy of the row, stored beside a conflict for the resolver. */
  readonly serverCurrent: unknown;
  /** What the person changed, for the resolver to show beside the server's copy. */
  readonly mine: unknown;
}

/** A photo thumbnail or a file kept for a property. */
export interface StoredBlob {
  readonly key: string;
  readonly blob: Blob;
  readonly propertyId: string;
  readonly sizeBytes: number;
}

/** What the copy knows about itself. */
export interface OfflineMeta {
  readonly key: string;
  /** The properties being kept, newest first. */
  readonly kept: readonly KeptProperty[];
  /** The manifest's `at` from the last refresh; null before the first one. */
  readonly syncedAt: string | null;
}

export interface KeptProperty {
  readonly propertyId: string;
  readonly name: string;
  readonly keptAt: string;
  /** Bytes of photos and files held for it. */
  readonly bytes: number;
  /** Photos or files too large to keep, named so the page can say what is missing. */
  readonly skipped: readonly string[];
  /**
   * Every node id the copy holds for this property. The manifest names what
   * moved by id, so this is how a refresh tells a change under this property
   * from a change somewhere else the device does not keep.
   */
  readonly nodeIds: readonly string[];
}

/** The empty copy a fresh database reads as. */
export const EMPTY_META: OfflineMeta = { key: '', kept: [], syncedAt: null };

function databaseName(userId: string): string {
  return `${OFFLINE_DB_PREFIX}${userId}`;
}

/**
 * Whether this browser will give us IndexedDB at all. A private window, or
 * one with site data blocked, has the property but throws on use; both are
 * answered here as "no copy", which every caller already handles.
 */
export function offlineStorageAvailable(): boolean {
  try {
    // The DOM types declare `indexedDB` as always present; a browser with
    // site data blocked does not provide it, and reading it can throw.
    return (globalThis.indexedDB as IDBFactory | undefined) !== undefined;
  } catch {
    return false;
  }
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => {
      resolve(request.result);
    });
    request.addEventListener('error', () => {
      reject(request.error ?? new Error('The offline copy could not be read'));
    });
  });
}

/** Open (creating or upgrading) the copy belonging to one account. */
export async function openOfflineDb(userId: string): Promise<IDBDatabase> {
  const request = indexedDB.open(databaseName(userId), OFFLINE_DB_VERSION);
  request.addEventListener('upgradeneeded', () => {
    const db = request.result;
    for (const name of Object.values(OFFLINE_STORES)) {
      if (db.objectStoreNames.contains(name)) {
        continue;
      }
      db.createObjectStore(
        name,
        name === OFFLINE_STORES.queue ? { keyPath: 'id', autoIncrement: true } : { keyPath: 'key' },
      );
    }
  });
  return await promisify(request);
}

/**
 * Delete an account's whole copy. Called on sign-out, on the 401 that says
 * the session is gone, and when a different account signs in on this device.
 * A connection still open would block the delete, so the caller closes first.
 */
export async function deleteOfflineDb(userId: string): Promise<void> {
  const request = indexedDB.deleteDatabase(databaseName(userId));
  await new Promise<void>((resolve, reject) => {
    request.addEventListener('success', () => {
      resolve();
    });
    // A blocked delete still completes once the other connection closes; the
    // copy is gone either way, so waiting for it would only hang sign-out.
    request.addEventListener('blocked', () => {
      resolve();
    });
    request.addEventListener('error', () => {
      reject(request.error ?? new Error('The offline copy could not be deleted'));
    });
  });
}

/**
 * Delete every account's copy on this origin but `userId`'s. Opening for an
 * account only knows about the copy this page itself opened; a copy left by
 * an account whose sign-out never ran here — the browser closed, the session
 * ended while the phone was away — would otherwise outlive the next sign-in
 * on a shared device. The origin's databases are listed and the rest removed.
 * A browser that cannot list them (none the product targets) is reported,
 * not hidden.
 */
export async function deleteOtherOfflineDbs(userId: string): Promise<void> {
  const keep = databaseName(userId);
  let listed: readonly IDBDatabaseInfo[];
  try {
    listed = await indexedDB.databases();
  } catch (error) {
    console.warn(
      'The browser cannot list its databases; another account’s copy may remain until it signs in here',
      error,
    );
    return;
  }
  for (const { name } of listed) {
    if (name !== undefined && name.startsWith(OFFLINE_DB_PREFIX) && name !== keep) {
      await deleteOfflineDb(name.slice(OFFLINE_DB_PREFIX.length));
    }
  }
}

type StoreName = (typeof OFFLINE_STORES)[keyof typeof OFFLINE_STORES];

/** Run `work` against one store and resolve once its transaction has committed. */
export async function withStore<T>(
  db: IDBDatabase,
  name: StoreName,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const transaction = db.transaction(name, mode);
  const done = new Promise<void>((resolve, reject) => {
    transaction.addEventListener('complete', () => {
      resolve();
    });
    transaction.addEventListener('error', () => {
      reject(transaction.error ?? new Error('The offline copy could not be written'));
    });
    transaction.addEventListener('abort', () => {
      reject(transaction.error ?? new Error('The offline write was abandoned'));
    });
  });
  const result = await work(transaction.objectStore(name));
  await done;
  return result;
}

/** One record by key, or null. */
export async function readOne<T>(store: IDBObjectStore, key: IDBValidKey): Promise<T | null> {
  return (await promisify<T | undefined>(store.get(key) as IDBRequest<T | undefined>)) ?? null;
}

/** Every record in a store, in key order. */
export async function readAll<T>(store: IDBObjectStore): Promise<T[]> {
  return await promisify<T[]>(store.getAll() as IDBRequest<T[]>);
}

/** Write one record; the store's key path names it. */
export async function writeOne(store: IDBObjectStore, value: unknown): Promise<IDBValidKey> {
  return await promisify(store.put(value));
}

/** Remove one record by key. */
export async function removeOne(store: IDBObjectStore, key: IDBValidKey): Promise<void> {
  await promisify(store.delete(key));
}
